// ============================================================================
// FILE 1: PointsConfig.js
// src/pages/LMS/Gamification/PointsConfig.js
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button,
  Switch, FormControlLabel, Divider, Alert, Stack,
  Card, CardContent, IconButton, Chip
} from '@mui/material';
import {
  Save as SaveIcon,
  EmojiEvents as TrophyIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const PointsConfig = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [config, setConfig] = useState({
    gamification_enabled: true,
    leaderboard_enabled: true,
    badges_enabled: true,
    points_course_complete: 100,
    points_module_complete: 10,
    points_test_pass: 20,
    points_perfect_score: 10,
    points_first_attempt_pass: 5,
    level_thresholds: [100, 250, 500, 1000, 2000, 5000, 10000]
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await axiosInstance.get('/api/lms/points/config');
      if (response.data.data) {
        setConfig({
          ...config,
          ...response.data.data,
          level_thresholds: response.data.data.level_thresholds || config.level_thresholds
        });
      }
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await axiosInstance.put('/api/lms/points/config', config);
      setMessage({ type: 'success', text: 'Configuration saved successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleLevelChange = (index, value) => {
    const newThresholds = [...config.level_thresholds];
    newThresholds[index] = parseInt(value) || 0;
    setConfig({ ...config, level_thresholds: newThresholds });
  };

  const addLevel = () => {
    const lastThreshold = config.level_thresholds[config.level_thresholds.length - 1] || 0;
    setConfig({
      ...config,
      level_thresholds: [...config.level_thresholds, lastThreshold + 5000]
    });
  };

  const removeLevel = (index) => {
    if (config.level_thresholds.length <= 1) return;
    const newThresholds = config.level_thresholds.filter((_, i) => i !== index);
    setConfig({ ...config, level_thresholds: newThresholds });
  };

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(`/chatbot/${chatbotId}/lms`)}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              <TrophyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Gamification Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure points, levels, and badges for your LMS
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          Save Changes
        </Button>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Feature Toggles */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Feature Toggles
        </Typography>
        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={config.gamification_enabled}
                onChange={(e) => setConfig({ ...config, gamification_enabled: e.target.checked })}
              />
            }
            label="Enable Gamification System"
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.leaderboard_enabled}
                onChange={(e) => setConfig({ ...config, leaderboard_enabled: e.target.checked })}
                disabled={!config.gamification_enabled}
              />
            }
            label="Enable Leaderboard"
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.badges_enabled}
                onChange={(e) => setConfig({ ...config, badges_enabled: e.target.checked })}
                disabled={!config.gamification_enabled}
              />
            }
            label="Enable Badges"
          />
        </Stack>
      </Paper>

      {/* Points Configuration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Points Values
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Configure how many points students earn for different actions
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Complete Course"
              value={config.points_course_complete}
              onChange={(e) => setConfig({ ...config, points_course_complete: parseInt(e.target.value) || 0 })}
              disabled={!config.gamification_enabled}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Complete Module"
              value={config.points_module_complete}
              onChange={(e) => setConfig({ ...config, points_module_complete: parseInt(e.target.value) || 0 })}
              disabled={!config.gamification_enabled}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Pass Test"
              value={config.points_test_pass}
              onChange={(e) => setConfig({ ...config, points_test_pass: parseInt(e.target.value) || 0 })}
              disabled={!config.gamification_enabled}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Perfect Score Bonus"
              value={config.points_perfect_score}
              onChange={(e) => setConfig({ ...config, points_perfect_score: parseInt(e.target.value) || 0 })}
              disabled={!config.gamification_enabled}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="First Attempt Pass Bonus"
              value={config.points_first_attempt_pass}
              onChange={(e) => setConfig({ ...config, points_first_attempt_pass: parseInt(e.target.value) || 0 })}
              disabled={!config.gamification_enabled}
              inputProps={{ min: 0 }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Level Thresholds */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6">
              Level Thresholds
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Points required to reach each level
            </Typography>
          </Box>
          <Button
            startIcon={<AddIcon />}
            onClick={addLevel}
            disabled={!config.gamification_enabled}
            size="small"
          >
            Add Level
          </Button>
        </Box>
        <Grid container spacing={2}>
          {config.level_thresholds.map((threshold, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Chip label={`Level ${index + 1}`} color="primary" size="small" />
                    {config.level_thresholds.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => removeLevel(index)}
                        disabled={!config.gamification_enabled}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <TextField
                    fullWidth
                    type="number"
                    value={threshold}
                    onChange={(e) => handleLevelChange(index, e.target.value)}
                    disabled={!config.gamification_enabled}
                    sx={{ mt: 2 }}
                    inputProps={{ min: 0 }}
                    label="Points Required"
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Preview */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Points Preview
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Example scenario: Student completes a course with 5 modules and passes all tests
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography>Complete 5 Modules</Typography>
            <Typography fontWeight={600}>
              {config.points_module_complete * 5} points
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography>Pass 5 Tests</Typography>
            <Typography fontWeight={600}>
              {config.points_test_pass * 5} points
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography>Complete Course</Typography>
            <Typography fontWeight={600}>
              {config.points_course_complete} points
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6">Total Earned</Typography>
            <Typography variant="h6" color="primary">
              {(config.points_module_complete * 5) + 
               (config.points_test_pass * 5) + 
               config.points_course_complete} points
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default PointsConfig;
